import { forwardRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Bar, BarChart, Line, LineChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Badge } from '@/components/ui/badge';
import type { AskResult } from '@/lib/admin/insights-api';

type Props = {
  question: string;
  result: AskResult;
};

export const AskResultView = forwardRef<HTMLDivElement, Props>(function AskResultView(
  { question, result },
  ref,
) {
  const safeSeries = (result.chart?.series ?? []).map((p: { label: unknown; value: unknown }) => ({
    label:
      typeof p.label === 'string' || typeof p.label === 'number'
        ? String(p.label)
        : JSON.stringify(p.label),
    value: typeof p.value === 'number' ? p.value : Number(p.value) || 0,
  }));

  return (
    <div ref={ref} className="bg-background border border-border rounded-md p-5 space-y-4">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Question
        </div>
        <div className="mt-1 text-sm font-medium">{question}</div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {new Date(result.generated_at || Date.now()).toLocaleString()}
        </div>
      </div>

      {result.insufficient_data && (
        <Badge variant="secondary">Insufficient data under k≥5 floor</Badge>
      )}

      <div className="prose prose-sm max-w-none dark:prose-invert">
        <ReactMarkdown>{result.answer_markdown ?? ''}</ReactMarkdown>
      </div>

      {result.chart && safeSeries.length > 0 && (
        <div className="pt-1">
          <div className="text-sm font-medium mb-1">{result.chart.title}</div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              {result.chart.type === 'line' ? (
                <LineChart data={safeSeries}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot />
                </LineChart>
              ) : (
                <BarChart data={safeSeries}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="hsl(var(--primary))" />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {result.citations && result.citations.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {result.citations.map((c, i) => (
            <Badge key={i} variant="outline" className="text-[10px]">
              {c}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
});
